import Load from 'components/Load';
import { createNavigationalRoute } from 'modules/app/components/NavigationalLayout';
import React from 'react';
import { Route } from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { isCordova } from 'utils/platform';

const AsyncHostPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-page" */
      './components/HostPage'
    ),
  {},
  true,
);

const AsyncContactHostSuccess = Load(
  () =>
    import(
      /* webpackChunkName: "contact-host-form" */ 'modules/host/containers/ContactHostSuccessContainer'
    ),
  {},
  true,
);
const AsyncContactHostSendingModal = Load(
  () =>
    import(
      /* webpackChunkName: "contact-host-form" */ 'modules/host/containers/ContactHostSendingModalContainer'
    ),
  {},
  true,
);
const AsyncContactHostForm = Load(
  () =>
    import(
      /* webpackChunkName: "contact-host-form" */ 'modules/host/containers/ContactFormContainer'
    ),
  {},
  true,
);
const AsyncExperiencesModal = Load(
  () =>
    import(
      /* webpackChunkName: "experiences-modal" */ './components/HostExperiences/modals/ExperienceModal'
    ),
  {},
  true,
);

const AsyncPicturesModal = Load(
  () =>
    import(
      /* webpackChunkName: "pictures-modal" */ './components/HostPictures/HostPicturesModal'
    ),
  {},
  true,
);

const AsyncHostReviewsPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-reviews-page" */ './components/HostReviewsPage'
    ),
  {},
  true,
);

export const hostRoute: RouteDescriptor = {
  id: 'host.profile',
  path: `${basePath}/host/:pretty_url`,
  label: 'Host Profile',
  audience: 'both',
  layout: 'navigational',
  module: 'host',
};

export const hostContactRoute: RouteDescriptor = {
  id: 'host.contact',
  path: `${basePath}/host/:pretty_url/contact/:form`,
  label: 'Contact Host',
  audience: 'both',
  layout: 'standalone',
  module: 'host',
};

export const hostContactSuccessRoute: RouteDescriptor = {
  id: 'host.contact.success',
  path: `${basePath}/host/:pretty_url/contact/:form/success`,
  label: 'Contact Host Success',
  audience: 'both',
  layout: 'standalone',
  module: 'host',
};

export const hostContactSendingRoute: RouteDescriptor = {
  id: 'host.contact.sending',
  path: `${basePath}/host/:pretty_url/contact/:form/sending`,
  label: 'Contact Host Sending',
  audience: 'both',
  layout: 'modal',
  module: 'host',
  modal: true,
};

export const hostExperiencesModalRoute: RouteDescriptor = {
  id: 'host.experiences.modal',
  path: `${basePath}/host/:pretty_url/experiences/`,
  label: 'Host Experiences Modal',
  audience: 'both',
  layout: 'modal',
  module: 'host',
  modal: true,
};

export const hostPicturesModalRoute: RouteDescriptor = {
  id: 'host.pictures.modal',
  path: `${basePath}/host/:pretty_url/pictures/`,
  label: 'Host Pictures Modal',
  audience: 'both',
  layout: 'modal',
  module: 'host',
  modal: true,
};

export const hostReviewsRoute: RouteDescriptor = {
  id: 'host.reviews',
  path: `${basePath}/host/:pretty_url/reviews/`,
  label: 'Host Reviews',
  audience: 'both',
  layout: 'navigational',
  module: 'host',
};

export const hostReviewsPaginatedRoute: RouteDescriptor = {
  id: 'host.reviews.paginated',
  path: `${basePath}/host/:pretty_url/reviews/page/:page/`,
  label: 'Host Reviews Paginated',
  audience: 'both',
  layout: 'navigational',
  module: 'host',
};

const isApp = isCordova();

export const hostRoutes = [
  createNavigationalRoute({
    key: hostContactSuccessRoute.id,
    id: hostContactSuccessRoute.id,
    path: hostContactSuccessRoute.path,
    handle: createRouteHandle(hostContactSuccessRoute),
    component: AsyncContactHostSuccess,
    hideFooter: true,
  }),
  <Route
    key={hostContactSendingRoute.id}
    id={hostContactSendingRoute.id}
    path={hostContactSendingRoute.path}
    handle={createRouteHandle(hostContactSendingRoute)}
    element={<AsyncContactHostSendingModal />}
  />,
  createNavigationalRoute({
    key: hostContactRoute.id,
    id: hostContactRoute.id,
    path: hostContactRoute.path,
    handle: createRouteHandle(hostContactRoute),
    component: AsyncContactHostForm,
    hideFooter: true,
  }),
  <Route
    key={hostExperiencesModalRoute.id}
    id={hostExperiencesModalRoute.id}
    path={hostExperiencesModalRoute.path}
    handle={createRouteHandle(hostExperiencesModalRoute)}
    element={<AsyncExperiencesModal />}
  />,
  <Route
    key={hostPicturesModalRoute.id}
    id={hostPicturesModalRoute.id}
    path={hostPicturesModalRoute.path}
    handle={createRouteHandle(hostPicturesModalRoute)}
    element={<AsyncPicturesModal />}
  />,
  createNavigationalRoute({
    key: hostReviewsRoute.id,
    id: hostReviewsRoute.id,
    path: hostReviewsRoute.path,
    handle: createRouteHandle(hostReviewsRoute),
    component: AsyncHostReviewsPage,
    showSearchInNavbar: true,
    noNavbar: isApp,
  }),
  createNavigationalRoute({
    key: hostReviewsPaginatedRoute.id,
    id: hostReviewsPaginatedRoute.id,
    path: hostReviewsPaginatedRoute.path,
    handle: createRouteHandle(hostReviewsPaginatedRoute),
    component: AsyncHostReviewsPage,
    showSearchInNavbar: true,
    noNavbar: isApp,
  }),
  createNavigationalRoute({
    key: hostRoute.id,
    id: hostRoute.id,
    path: hostRoute.path,
    handle: createRouteHandle(hostRoute),
    component: AsyncHostPage,
    noNavbar: isApp,
  }),
];

export const hostRouteDescriptors = [
  hostRoute,
  hostContactRoute,
  hostContactSuccessRoute,
  hostContactSendingRoute,
  hostExperiencesModalRoute,
  hostPicturesModalRoute,
  hostReviewsRoute,
  hostReviewsPaginatedRoute,
];

export default hostRoutes;
